import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import useTranslation from "@/hooks/use_translation";

import { Body, SectionTitle } from "@/sprout/typography";
import Container from "@/components/ui/container";
import { SearchBar } from "@components/job_search/search_bar.tsx";
import { router, usePage } from "@inertiajs/react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ApplicationForm } from "@components/job_post/application_form";
import { ApplicationSuccess } from "@components/job_post/application_success";
import DiscardProgressModal from "@components/job_post/discard_progress_modal";
import type { JobPost as JbenJobPost, SearchSpecificFields } from "@/types/jben/job_post";
import type { BoardConfiguration } from "@/types/jben/board_configuration";
import { useEffectAfterMount } from "@hooks/use_effect_after_mount.ts";
import JobSearchTable from "@components/job_search/table.tsx";
import useDevice from "@hooks/use_device.ts";
import HelpPopover from "@sprout/help_popover.tsx";
import { SnowplowEvent, useSnowplowTracker } from "@hooks/use_tracker.ts";
import { type Location } from "@components/ui/location_input.tsx";
import { type RecentSearch } from "@/types/job_search/recent_searches";
import { useJobSearch, buildJobsParams, recentSearchToLocation } from "@hooks/use_job_search";
import { useSafePostHog } from "@hooks/use_posthog";
import { JobSearchFilters } from "@components/job_search/job_search_filters.tsx";

export type JobPost = {
  id: string;
  title: string;
  companyName: string;
  logoUrl: string;
  publicUrl: string;
  viewJobPath: string | null;
  firstPublished: string;
  locations: string[];
  workType: string | null;
  payRanges: string;
  viewed: boolean;
  appliedAt: string | null;
};

export type SearchParams = {
  query?: string;
  location?: string;
  lat?: number;
  lon?: number;
  location_type?: string;
  country_short_name?: string;
  state_short_name?: string;
  date_posted?: string;
  salary?: string;
  work_type?: string[];
  employment_type?: string[];
  search_source?: "popover";
};

type Props = {
  jobPosts: JobPost[] | undefined;
  moreResultsAvailable: boolean;
  page: number;
  browsing: boolean;
  defaultJobFiltersEnabled: boolean;
  trackingData: { [key: string]: string };
  jobPost?: JbenJobPost & SearchSpecificFields;
  boardConfiguration?: BoardConfiguration;
  submitPath?: string;
  recentSearches: RecentSearch[];
  educationApiUrl?: string;
  dreamJobAvailable?: boolean;
};

const buildLocation = (urlParams: URLSearchParams): Location | null => {
  if (!urlParams.get("location") || !urlParams.get("lat") || !urlParams.get("lon")) {
    return null;
  }

  return {
    display_name: urlParams.get("location") || "",
    latitude: Number(urlParams.get("lat")) || 0,
    longitude: Number(urlParams.get("lon")) || 0,
    country_short_name: urlParams.get("country_short_name") || null,
    state_short_name: urlParams.get("state_short_name") || null,
    location_type: urlParams.get("location_type") || null,
  };
};

const JobSearch = ({
  jobPosts,
  moreResultsAvailable,
  page,
  browsing,
  defaultJobFiltersEnabled,
  trackingData,
  jobPost,
  boardConfiguration,
  submitPath,
  recentSearches,
  educationApiUrl,
  dreamJobAvailable,
}: Props) => {
  const sharedReferrer = (usePage().props as { referrer?: string | null }).referrer;
  const jobPostParametersPresent = jobPost && boardConfiguration && submitPath;
  const [isModalOpen, setIsModalOpen] = useState(!!jobPostParametersPresent);
  const [submitted, setSubmitted] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const needsNavigationOnClose = useRef(!!jobPostParametersPresent);
  const dirtyRef = useRef<() => boolean>(() => false);
  useEffect(() => {
    if (jobPost) setIsModalOpen(true);
    setSubmitted(false);
  }, [jobPost?.fingerprint]);
  const closeModal = () => {
    setIsModalOpen(false);
    setSubmitted(false);
    setShowDiscardConfirm(false);
    if (needsNavigationOnClose.current) {
      needsNavigationOnClose.current = false;
      if (sharedReferrer && sharedReferrer.includes("/jobs")) {
        router.visit(sharedReferrer, { preserveScroll: true, preserveState: true });
      } else {
        router.visit(`/jobs${window.location.search}`, {
          preserveScroll: true,
          preserveState: true,
          only: ["defaultJobFiltersEnabled"],
        });
      }
    }
  };
  const urlSearchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const [location, setLocation] = useState<Location | null>(buildLocation(urlSearchParams));
  const [query, setQuery] = useState(urlSearchParams.get("query") || "");
  const tracker = useSnowplowTracker();
  const submitJobSearch = useJobSearch();
  const posthog = useSafePostHog();

  const { t } = useTranslation("jobSearch");
  const device = useDevice();
  const [loading, setLoading] = useState(false);
  const [datePosted, setDatePosted] = useState(urlSearchParams.get("date_posted") || "");
  const [salary, setSalary] = useState(urlSearchParams.get("salary") || "");
  const [workType, setWorkType] = useState<string[]>(urlSearchParams.getAll("work_type[]"));
  const [employmentType, setEmploymentType] = useState<string[]>(
    urlSearchParams.getAll("employment_type[]"),
  );
  const isBrowsing = (typeof browsing === "undefined" && !query) || browsing;
  const browseKey = defaultJobFiltersEnabled ? "browseWithDefaults" : "browse";
  const resultsKey = defaultJobFiltersEnabled ? "resultsWithDefaults" : "results";
  const [searchResultsText, setSearchResultsText] = useState<string>(
    isBrowsing ? t(browseKey) : t(resultsKey),
  );
  const [searchSubtext, setSearchSubtext] = useState<string>(
    defaultJobFiltersEnabled && isBrowsing ? t("browseSubtext") : "",
  );
  const [noResultsMessage, setNoResultsMessage] = useState<string>("");

  useEffect(() => {
    if (typeof jobPosts === "undefined") return;

    if (jobPosts.length > 0) {
      if (browsing) {
        setSearchResultsText(t(browseKey));
        setSearchSubtext(defaultJobFiltersEnabled ? t("browseSubtext") : "");
      } else {
        setSearchResultsText(t(resultsKey));
        setSearchSubtext("");
      }
      return;
    }

    if (query) {
      setSearchResultsText(t("noResultsForSearchTerm", { searchTerm: query }));
      setNoResultsMessage(t("noResultsForSearchTermMessage"));
      setSearchSubtext("");
      return;
    }

    setSearchResultsText(t("noResults"));
    setNoResultsMessage(t("noResultsMessage"));
    setSearchSubtext("");
  }, [jobPosts, browsing]);

  const searchParams: SearchParams = buildJobsParams(query, location, {
    date_posted: datePosted,
    salary,
    work_type: workType,
    employment_type: employmentType,
  });

  useEffect(() => {
    posthog?.capture("job_search_submitted", searchParams);
  }, []);

  const handleSearch = () => {
    setLoading(true);
    jobPosts = undefined;

    submitJobSearch(searchParams, {
      onFinish: () => setLoading(false),
    });
  };

  const handleRecentSearchSelect = (search: RecentSearch) => {
    setLoading(true);

    submitJobSearch(
      buildJobsParams(search.query || undefined, recentSearchToLocation(search), search, "popover"),
      { onFinish: () => setLoading(false) },
    );
  };

  useEffectAfterMount(() => {
    handleSearch();
  }, [datePosted, salary, workType, employmentType]);

  const createTrackSearchResultClick = useCallback(
    (post: JobPost) => {
      return () => {
        tracker.trackEvent(SnowplowEvent.SearchResultClicked, {
          job_post_id: post.id,
          job_post_title: post.title,
          ...trackingData,
        });
        posthog?.capture("card_clicked", {
          card_type: "job",
          source: "job_search",
          job_post_id: post.id,
          job_title: post.title,
          company_name: post.companyName,
          previously_viewed: post.viewed,
        });
      };
    },
    [trackingData, posthog],
  );

  const handleJobView = (jobPostId: string) => {
    if (!jobPosts) return;

    const updatedJobPosts = jobPosts.map((post) =>
      post.id === jobPostId ? { ...post, viewed: true } : post,
    );

    router.replaceProp("jobPosts", updatedJobPosts);
  };

  const handleApplySuccess = () => {
    setSubmitted(true);

    if (!jobPosts || !jobPost) return;

    const appliedAt = new Date().toISOString();
    const updatedJobPosts = jobPosts.map((post) =>
      post.id === jobPost.id ? { ...post, appliedAt } : post,
    );

    router.replaceProp("jobPosts", updatedJobPosts);
  };

  return (
    <>
      <div className="py-9 pl-7 pr-6 bg-green-100">
        <SearchBar
          onSearch={handleSearch}
          query={query}
          onQueryChange={setQuery}
          location={location}
          onLocationChange={setLocation}
          disabled={loading}
          recentSearches={recentSearches}
          onRecentSearchSelect={handleRecentSearchSelect}
        />
      </div>
      <JobSearchFilters
        datePosted={datePosted}
        onDatePostedChange={setDatePosted}
        salary={salary}
        onSalaryChange={setSalary}
        workType={workType}
        onWorkTypeChange={setWorkType}
        employmentType={employmentType}
        onEmploymentTypeChange={setEmploymentType}
      />
      <Container className="!max-w-[1550px] !pt-0">
        <div className="flex flex-row items-center gap-2">
          <SectionTitle large>{searchResultsText}</SectionTitle>
          {device.isMobile && <HelpPopover text={t("aiDisclaimer")} />}
        </div>
        {searchSubtext && <Body>{searchSubtext}</Body>}
        <JobSearchTable
          jobPosts={jobPosts}
          browsing={browsing}
          moreResultsAvailable={moreResultsAvailable}
          currentPage={page}
          lastPublishDate={jobPosts?.at(-1)?.firstPublished || null}
          emptyStateMessage={noResultsMessage}
          searchParams={searchParams}
          createTrackSearchResultClick={createTrackSearchResultClick}
          onView={handleJobView}
        />
      </Container>
      {isModalOpen && <div className="fixed inset-0 z-40 bg-gray-900/30" aria-hidden="true" />}
      {jobPostParametersPresent && (
        <Dialog
          open={isModalOpen}
          modal={false}
          onOpenChange={(open) => {
            if (open) return;
            if (dirtyRef.current() && !submitted) {
              setShowDiscardConfirm(true);
            } else {
              closeModal();
            }
          }}
        >
          <DialogContent
            className="w-[90vw] sm:max-w-[900px] max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0"
            hideClose={false}
            closeButtonClassName="z-10 rounded-full bg-white! opacity-100 shadow-md [&>svg]:size-8"
          >
            <div className="min-h-0 overflow-y-auto p-8">
              {submitted ? (
                <ApplicationSuccess jobPost={jobPost} />
              ) : (
                <ApplicationForm
                  jobPost={jobPost}
                  questions={jobPost.questions}
                  submitPath={submitPath}
                  boardConfiguration={boardConfiguration}
                  onSuccess={handleApplySuccess}
                  educationApiUrl={educationApiUrl}
                  dreamJobAvailable={dreamJobAvailable}
                  dirtyRef={dirtyRef}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
      <DiscardProgressModal
        open={showDiscardConfirm}
        onOpenChange={setShowDiscardConfirm}
        onDiscard={closeModal}
      />
    </>
  );
};

export default JobSearch;
